import { removeLastUrlSegment } from '@my-pages/utils/urlUtils';
import { Card, CardBody, CardHeader, CardTitle } from '@shared/components/Card';
import Markup from '@shared/components/Markup';
import { useGetUmbracoTafDeliveryApiV1ContentItemPathSuspense } from '@shared/integrations/umbraco-taf-delivery-api/features/content/content';
import { AccommodationBookingGroupPageContentResponseModel, AccommodationBookingObjectContentResponseModel } from '@shared/integrations/umbraco-taf-delivery-api/models';
import BookingObjectImages from '@my-pages/features/object-booking/components/BookingObjectImages.tsx';
import CabinObjectMetaData from '@my-pages/features/object-booking/components/CabinObjectMetaData.tsx';
import CabinBookingObjectAccommodation from '@my-pages/features/object-booking/components/CabinBookingObjectAccommodation.tsx';
import AccommodationBookingForm from '@my-pages/features/object-booking/components/AccommodationBookingForm.tsx';
import BookingObjectAttachments from '@my-pages/features/object-booking/components/BookingObjectAttachments.tsx';
import AlertBox from '@shared/components/AlertBox.tsx';

interface IAccommodationBookingObjectPage {
    content: AccommodationBookingObjectContentResponseModel;
}

const AccommodationBookingObjectPage = ({ content }: IAccommodationBookingObjectPage) => {
    const groupUrl = removeLastUrlSegment(content.route?.path ?? '');
    const { data: cabinGroup } = useGetUmbracoTafDeliveryApiV1ContentItemPathSuspense<AccommodationBookingGroupPageContentResponseModel>(groupUrl);

    return (
        <div className='d-flex flex-column gap-4'>
            <button
                type='button'
                className='btn btn-link d-flex align-items-center align-self-start px-0 mb-n3'
                onClick={() => window.history.back()}
            >
                <span className='mdi mdi-arrow-left font-size-xl'></span>
                <span>Tillbaka</span>
            </button>
            {content.properties?.bookingObjectId ? null : (
                <AlertBox theme='danger'>
                    <h5 className='card-title'>Tillfälligt bokningsproblem</h5>
                    <p>Denna stuga går för tillfället inte att boka då kopplingen till bokningssystemet saknas. Vi arbetar på att lösa problemet.</p>
                </AlertBox>
            )}
            {content.properties?.images ? (
                <BookingObjectImages
                    modalTitle={`Bilder på ${content.name}`}
                    images={content.properties?.images}
                />
            ) : null}
            <header>
                {content.properties?.location ? <div className='kicker font-size-sm'>{content.properties.location}</div> : null}
                <h1 className='mb-0'>{content.name}</h1>
                <CabinObjectMetaData content={content} />
            </header>
            <section>
                <div className='row row-gap-4'>
                    <div className='col-xl-8'>
                        <div className='d-flex flex-column gap-4'>
                            {content.properties?.description?.markup ? (
                                <Card>
                                    <CardHeader>
                                        <CardTitle variant='h3'>Om boendet</CardTitle>
                                    </CardHeader>
                                    <CardBody>
                                        <Markup content={content.properties.description.markup} />
                                    </CardBody>
                                </Card>
                            ) : null}

                            {content.properties?.includedAccommodation?.items || cabinGroup.properties?.accommodation?.items ? (
                                <Card>
                                    <CardHeader>
                                        <CardTitle variant='h3'>Faciliteter</CardTitle>
                                    </CardHeader>
                                    <CardBody>
                                        <CabinBookingObjectAccommodation
                                            cabinAccommodations={content.properties?.includedAccommodation?.items}
                                            groupAccommodations={cabinGroup.properties?.accommodation?.items}
                                        />
                                    </CardBody>
                                </Card>
                            ) : null}

                            <div className=' d-flex flex-wrap flex-row row-gap-4'>
                                {cabinGroup.properties?.generalRules?.markup ? (
                                    <Card>
                                        <CardHeader>
                                            <CardTitle variant='h3'>Regler</CardTitle>
                                        </CardHeader>
                                        <CardBody>
                                            <Markup content={cabinGroup.properties.generalRules.markup} />
                                        </CardBody>
                                    </Card>
                                ) : null}
                                {cabinGroup.properties?.checkInCheckOutDescription?.markup ? (
                                    <Card>
                                        <CardHeader>
                                            <CardTitle variant='h3'>In- och utcheckning </CardTitle>
                                        </CardHeader>
                                        <CardBody>
                                            <Markup content={cabinGroup.properties.checkInCheckOutDescription.markup} />
                                        </CardBody>
                                    </Card>
                                ) : null}
                                {cabinGroup.properties?.areaDescription?.markup ? (
                                    <Card>
                                        <CardHeader>
                                            <CardTitle variant='h3'>Området</CardTitle>
                                        </CardHeader>
                                        <CardBody>
                                            <Markup content={cabinGroup.properties.areaDescription.markup} />
                                        </CardBody>
                                    </Card>
                                ) : null}
                                {cabinGroup.properties?.cancellationPolicy?.markup ? (
                                    <Card
                                        id='cancellationPolicy'
                                        tabIndex={-1}
                                    >
                                        <CardHeader>
                                            <CardTitle variant='h3'>Bokningsvillkor</CardTitle>
                                        </CardHeader>
                                        <CardBody>
                                            <Markup content={cabinGroup.properties.cancellationPolicy.markup} />
                                        </CardBody>
                                    </Card>
                                ) : null}
                                {cabinGroup.properties?.miscInformation?.markup ? (
                                    <Card>
                                        <CardHeader>
                                            <CardTitle variant='h3'>Övrig information</CardTitle>
                                        </CardHeader>
                                        <CardBody>
                                            <Markup content={cabinGroup.properties.miscInformation.markup} />
                                        </CardBody>
                                    </Card>
                                ) : null}

                                {cabinGroup.properties?.attachments?.items ? (
                                    <Card>
                                        <CardHeader>
                                            <CardTitle variant='h3'>Bilagor</CardTitle>
                                        </CardHeader>
                                        <CardBody>
                                            <BookingObjectAttachments attachments={cabinGroup.properties?.attachments?.items} />
                                        </CardBody>
                                    </Card>
                                ) : null}
                            </div>
                        </div>
                    </div>
                    <div className='col-xl-4'>
                        <Card>
                            <CardBody className='d-flex flex-column gap-4'>
                                {content.properties?.bookingObjectId ? (
                                    <AccommodationBookingForm
                                        cabinGroup={cabinGroup}
                                        bookingObjectId={content.properties?.bookingObjectId}
                                    />
                                ) : (
                                    <AlertBox theme='danger'>
                                        <h5 className='card-title'>Tillfälligt bokningsproblem</h5>
                                        <p>Denna stuga går för tillfället inte att boka då kopplingen till bokningssystemet saknas. Vi arbetar på att lösa problemet.</p>
                                    </AlertBox>
                                )}
                            </CardBody>
                        </Card>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default AccommodationBookingObjectPage;
